// src/components/ResultModal.jsx
import { Button } from "@/components/ui/button";
import { Calendar, CheckCircle, DollarSign, FileText, X, XCircle } from "lucide-react";

const ResultModal = ({ 
  isOpen, 
  onClose, 
  success,
  title,
  data = {} // { bills_created, total_revenue, period, message }
}) => {
  if (!isOpen) return null;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50" 
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b ${success ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex items-center space-x-3">
              {success ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
              <h3 className={`text-xl font-semibold ${success ? 'text-green-900' : 'text-red-900'}`}>
                {title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {success ? (
              <div className="space-y-4">
                {/* Success Summary Cards */}
                <div className="grid grid-cols-1 gap-4">
                  {data.bills_created !== undefined && (
                    <div className="flex items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <FileText className="w-6 h-6 text-blue-600 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">Fatture Create</p>
                        <p className="text-2xl font-bold text-blue-800">{data.bills_created}</p>
                      </div>
                    </div>
                  )}

                  {data.total_revenue !== undefined && (
                    <div className="flex items-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <DollarSign className="w-6 h-6 text-green-600 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-green-900">Ricavo Totale</p>
                        <p className="text-2xl font-bold text-green-800">
                          {formatCurrency(data.total_revenue)}
                        </p>
                      </div>
                    </div>
                  )}

                  {data.period && (
                    <div className="flex items-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <Calendar className="w-6 h-6 text-purple-600 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-purple-900">Periodo</p>
                        <p className="text-lg font-semibold text-purple-800">{data.period}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Message */}
                {data.message && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {data.message}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-red-800 leading-relaxed">
                    {data.message || 'Si è verificato un errore durante l\'operazione.'}
                  </p>
                </div>
                {data.details && (
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <p className="text-xs text-gray-600 font-mono">{data.details}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-4 border-t bg-gray-50">
            <Button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white">
              Chiudi
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultModal;